/**
 * Display-order override for the Cash Flow Statement view.
 *
 * The Excel (Data/Cashflow_Apr-May 2026.xlsx → "Апрель 2026" sheet) has
 * a specific entity ordering and subtotal grouping that doesn't line up
 * with fin.entities.parent_entity (where `wwc.parent_entity = 'wwg'`,
 * not 'wwpi'). Rather than reshape the DB — which the Entity Registry
 * page already depends on — we keep a cash-flow-specific override here.
 *
 * Update this file when treasury adds or reorganises a company.
 */

export type StatementRowSpec =
  | { kind: 'group'; label: string }
  | { kind: 'entity'; entityId: string; label?: string; depth?: number }
  | { kind: 'subtotal'; label: string; entityIds: string[] }
  | { kind: 'grand-total'; label: string; entityIds: string[] };

/**
 * Row ordering that mirrors "Апрель 2026" row-by-row. `entityId` must
 * match `fin.entities.id`. Unknown ids are rendered greyed-out with
 * zero totals so treasury sees immediately which rows are missing DB
 * entities.
 *
 * `depth: 1` draws the row with a leading `└` indent, as in the Excel.
 * Subtotal rows (`kind: 'subtotal'`) sum the listed entity ids.
 */
export const STATEMENT_LAYOUT: StatementRowSpec[] = [
  { kind: 'group', label: 'ГРУППА WWG · WWG Group' },
  { kind: 'entity', entityId: 'wwg',     label: 'WWG (Холдинг)' },
  { kind: 'entity', entityId: 'wwf',     label: 'WWF' },
  { kind: 'entity', entityId: 'wwpi',    label: 'WWPI' },
  { kind: 'entity', entityId: 'wwc',     label: 'WWC',          depth: 1 },
  { kind: 'entity', entityId: 'ybi',     label: 'Yellow Black', depth: 1 },
  // Subtotal of the two direct sub-entities under WWPI in the Excel
  { kind: 'subtotal', label: 'WWC (итого)', entityIds: ['wwc', 'ybi'] },

  { kind: 'group', label: 'ГРУППА TRIA · Tria (Vault) Group' },
  // Custody sub-cluster (indented in the Excel)
  { kind: 'entity', entityId: 'wwmkt',     label: 'WW Digital',   depth: 1 },
  { kind: 'entity', entityId: 'tria_ft',   label: 'Tria Fintech', depth: 1 },
  { kind: 'entity', entityId: 'tria_sw',   label: 'Tria Software', depth: 1 },
  { kind: 'entity', entityId: 'sz',        label: 'SZ',           depth: 1 },
  { kind: 'subtotal', label: 'CUSTODY (итого)',
    entityIds: ['wwmkt', 'tria_ft', 'tria_sw', 'sz'] },
  { kind: 'entity', entityId: 'tria_hold', label: 'Tria Finance' },
  { kind: 'entity', entityId: 'tb',        label: 'Tria Bridge' },
  { kind: 'entity', entityId: 'cayman_spc', label: 'Cayman Fund' },

  { kind: 'group', label: 'ПРОЧИЕ КОМПАНИИ · Other Companies' },
  { kind: 'entity', entityId: 'wwmkt',  label: 'WW Market' },
  { kind: 'entity', entityId: 'wwcm',   label: 'WW UAE' },
  { kind: 'entity', entityId: 'wwcorp', label: 'WW Corp' },

  // Group totals + grand total at the bottom
  { kind: 'subtotal', label: 'ИТОГО ГРУППА WWG',
    entityIds: ['wwg', 'wwf', 'wwpi', 'wwc', 'ybi'] },
  { kind: 'subtotal', label: 'ИТОГО ГРУППА TRIA',
    entityIds: ['wwmkt', 'tria_ft', 'tria_sw', 'sz', 'tria_hold', 'tb', 'cayman_spc'] },
  { kind: 'subtotal', label: 'ИТОГО ПРОЧИЕ КОМПАНИИ',
    entityIds: ['wwmkt', 'wwcm', 'wwcorp'] },
  { kind: 'grand-total', label: 'ИТОГО ПО ГРУППЕ',
    entityIds: [
      'wwg', 'wwf', 'wwpi', 'wwc', 'ybi',
      'wwmkt', 'tria_ft', 'tria_sw', 'sz', 'tria_hold', 'tb', 'cayman_spc',
      'wwcm', 'wwcorp',
    ] },
]
